<template>
  <UIDialog
    :is-open
    :z-index
    :title
    size="lg"
    flat
    @close="handleClose"
  >
    <template
      v-if="isChanges"
      #title
    >
      <div class="module-rules-form__title">
        <span>Preview changes</span>
        <span
          v-if="criteria"
          class="module-rules-form__criteria"
        >If {{ criteria }}</span>
      </div>
    </template>
    <RulesForm
      :rule
      :is-changes
      :get-filled-actions
      :map-criteria
      :prefilled
      :disabled
      @fetch:transactions="handleFetchTransactions"
      @create="handleCreate"
      @update="handleUpdate"
      @apply="handleApply"
      @close="handleClose"
    >
      <template #actions="{ actions, updateActions }">
        <TaggingRulesActions
          :model-value="actions"
          :disabled
          @update:model-value="updateActions"
        />
      </template>
      <template #change="{ list, actions }">
        <Change
          v-for="item in list"
          :key="item.id"
          :item
          :actions
        />
      </template>
    </RulesForm>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import {
  LinkedDataTransaction,
  ModalClass,
  PaginatedResponse,
  TransactionRule,
  TransactionRuleCriteriaType,
  RuleApplyStatus
} from '@types'
import { TaggingRuleAction } from './utils/types'

import { NOTIFICATION_DELAY } from '@/const/common'

import { useNotifications } from '@/plugins/notification'

import { mapChangeCriteria } from '@/modules/Rules/utils/helpers'
import { handleCatchedError } from '@/helpers/common'
import { getRuleCreationStatusMessage, getRuleCreationNotificationType, getRuleApplyStatusMessage, getRuleApplyNotificationType } from '@/store/linkedData/utils/helpers'

import { useLinkedDataTaggingRulesStore } from '@/store/linkedData/rules/tagging'

import RulesForm from '@/modules/Rules/RulesForm.vue'
import Change from './components/Change.vue'
import TaggingRulesActions from './TaggingRulesActions.vue'
import { UIDialog } from '@ui/modals'

type Props = {
  modal: ModalClass<any>
  rule?: TransactionRule<TaggingRuleAction[]>
  isChanges?: boolean
  prefilled?: boolean
}

type Emits = {
  fetch: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const linkedDataTaggingRulesStore = useLinkedDataTaggingRulesStore()

const title = computed(() => (props.rule?.id ? 'Edit rule' : 'New rule'))

const criteria = computed(
  () => props.rule && mapChangeCriteria(props.rule.data.criteria),
)

const zIndex = computed(() => props.modal.zIndex)
const isOpen = computed(() => props.modal.isOpened)

const disabled = computed(() => linkedDataTaggingRulesStore.loadingAction)

const data = computed(() => linkedDataTaggingRulesStore.getList)

const getFilledActions = (actions: TaggingRuleAction[]) => {
  return actions.filter(action => !!(action.value ?? action.tag_ids))
}

const mapCriteria = (criteria: TransactionRuleCriteriaType[]) => {
  return criteria.filter(
    item =>
      ![TransactionRuleCriteriaType.ACCESS_METHOD].includes(
        item,
      ),
  )
}

const handleFetchTransactions = async (
  data: TransactionRule<TaggingRuleAction[]>['data'],
  pageNo: number,
  pageSize: number,
  callback: (data: PaginatedResponse<LinkedDataTransaction>) => void,
  signal?: AbortSignal,
) => {
  try {
    const result = await linkedDataTaggingRulesStore.fetchTransactions(
      data,
      pageNo,
      pageSize,
      signal,
    )
    callback(result)
  } catch (e) {
    handleCatchedError(e as string, { data, pageNo, pageSize })
  }
}

const handleCreate = async (
  ruleData: TransactionRule<TaggingRuleAction[]>['data'],
  apply_to_existing_transactions: boolean,
  successCallback: (customMessage?: string) => void,
  errorCallback: () => void,
) => {
  try {
    const order = data.value.at(-1)?.order || 0
    const createdRule = await linkedDataTaggingRulesStore.store({
      order: order + 1,
      data: ruleData,
      apply_to_existing_transactions,
    })

    // Get appropriate message and notification type based on apply_status
    const message = getRuleCreationStatusMessage(createdRule?.apply_status)
    const notificationType = getRuleCreationNotificationType(createdRule?.apply_status)

    // Handle different status types appropriately for rule creation
    if (notificationType === 'success') {
      // For success or no apply status, use the success callback with custom message
      successCallback(message)
    } else {
      // For warnings/errors, clear progress and show custom notification
      errorCallback()

      // Show custom notification with appropriate type
      const { [notificationType]: showNotification } = useNotifications()
      await showNotification({ message }, NOTIFICATION_DELAY)
    }

    emit('fetch')
    handleClose()
  } catch {
    errorCallback()
  }
}

const handleUpdate = async (
  id: string,
  ruleData: TransactionRule<TaggingRuleAction[]>,
  successCallback: () => void,
  errorCallback: () => void,
) => {
  try {
    await linkedDataTaggingRulesStore.update(id, ruleData)
    successCallback()
  } catch (e) {
    errorCallback()
    handleCatchedError(e as string, ruleData)
  }
}

const handleApply = async (
  ruleData: TransactionRule<TaggingRuleAction[]>['data'],
  successCallback: (customMessage?: string) => void,
  errorCallback: () => void,
) => {
  try {
    const result = await linkedDataTaggingRulesStore.apply(ruleData)

    // Get the appropriate message and notification type
    const message = getRuleApplyStatusMessage(result.apply_status)
    const notificationType = getRuleApplyNotificationType(result.apply_status)

    // Handle different status types appropriately
    if (result.apply_status === RuleApplyStatus.SUCCESS) {
      // For success, use the success callback with custom message
      successCallback(message)
      handleClose()
    } else {
      // For non-success statuses, clear progress and show custom notification
      errorCallback()

      // Show custom notification with appropriate type
      const { [notificationType]: showNotification } = useNotifications()
      await showNotification({ message }, NOTIFICATION_DELAY)

      // Don't close modal for failed/partial/not_applied so user can adjust
    }
  } catch {
    errorCallback()
  }
}

const handleClose = () => {
  props.modal.close()
}
</script>
